
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ImageBackground,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import PrimaryButton from '@/components/PrimaryButton';
import Avatar from '@/components/Avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { TempOnboardingService } from '@/services/tempOnboardingService';
import { GalleryConsentService } from '@/services/galleryConsentService';
import { ImageUploadService } from '@/services/imageUploadService';
import { apiService } from '@/services/apiService';

const FALLBACK_COUNT = 1000;

export default function OnboardingSuccessScreen() {
  const router = useRouter();
  const { user, userProfile, refreshProfile } = useAuth();
  const { t, language } = useLanguage();
  const insets = useSafeAreaInsets();
  // Google users already have a photo from their Google account (the server
  // maps it into avatar_url on complete-profile). We keep the same success
  // screen for everyone, but for Google users we start it in the "photo already
  // uploaded" state (Continue button visible, avatar tappable to change it),
  // while email users start in the "to be uploaded" state.
  const isGoogleUser = user?.app_metadata?.provider === 'google';
  const [userName, setUserName] = useState('');
  const [userCount, setUserCount] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [avatarUri, setAvatarUri] = useState<string | null>(null);
  const [hasUploadedPhoto, setHasUploadedPhoto] = useState(false);
  // Apple 5.1.1 compliance — no exit affordance may be shown BEFORE the OS
  // permission prompt. This flag flips to true only after
  // requestPermissionAndPickImage() resolves (granted, denied, or cancelled),
  // at which point we may render a "Continuar" button so the user is never
  // trapped if they decline.
  const [permissionPromptShown, setPermissionPromptShown] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Same stale-profile guard as the userProfile effect below: don't
        // read full_name from a profile that doesn't belong to the
        // currently-authenticated user (can happen briefly after
        // signOut → signUp on the same device).
        const profileBelongsToCurrentUser =
          !!userProfile && !!user?.id && userProfile.id === user.id;

        if (profileBelongsToCurrentUser && userProfile?.full_name) {
          setUserName(userProfile.full_name);
        } else {
          const tempData = await TempOnboardingService.getData();
          if (tempData.name) {
            setUserName(tempData.name);
          }
        }
      } catch (error) {
        console.warn('Failed to load user name:', error);
      }

      try {
        const result = await apiService.get<{ count: number }>('/api/users/count');
        if (result.success && result.data?.count) {
          setUserCount(result.data.count);
        }
      } catch (error) {
        console.warn('Failed to load user count:', error);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    // Guard: ignore stale userProfile from a previous session. If the
    // profile in context doesn't belong to the currently-authenticated user
    // (can happen briefly after signOut → signUp on the same device), skip
    // it so we don't render the previous user's name or avatar.
    if (userProfile && user?.id && userProfile.id !== user.id) {
      return;
    }
    if (userProfile?.full_name) {
      setUserName(userProfile.full_name);
    }
    if (userProfile?.avatar_url) {
      setAvatarUri(userProfile.avatar_url);
    }
    // Google users already arrive with a photo from their Google account, so we
    // put the screen in the "photo already uploaded" state: the Continue button
    // shows immediately and they can still tap the avatar to change it. Email
    // users keep the "to be uploaded" state and pick a photo first.
    if (isGoogleUser) {
      setHasUploadedPhoto(true);
    }
  }, [userProfile, user?.id, isGoogleUser]);

  const firstName = userName.split(' ')[0] || userName;

  const formatUserCount = (count: number): string => {
    if (count <= 0) return '1.000';
    const localeMap: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };
    return count.toLocaleString(localeMap[language] || 'pt-BR');
  };

  const handleUploadPhoto = useCallback(async () => {
    if (imageLoading || !user?.id) return;

    const previousUri = avatarUri;
    setImageLoading(true);
    let imageUri: string | null = null;
    try {
      // Apple 5.1.1: this call goes straight to the OS permission prompt with
      // no intermediate custom modal. We capture both granted+picked and
      // granted+cancelled / denied outcomes so the post-prompt "Continuar"
      // affordance can surface in the finally branch.
      try {
        imageUri = await GalleryConsentService.requestPermissionAndPickImage();
      } catch (permissionError) {
        // requestPermissionAndPickImage throws when the user rejects
        // permission. That's a valid outcome — the prompt was shown, so we
        // still let the post-prompt continue button surface.
        console.warn('Gallery permission rejected:', permissionError);
        imageUri = null;
      }

      if (!imageUri) {
        return;
      }

      const validation = ImageUploadService.validateImageFile(imageUri);
      if (!validation.isValid) {
        console.warn('Invalid image:', validation.error);
        return;
      }

      setAvatarUri(imageUri);

      const result = await ImageUploadService.uploadProfilePicture(user.id, imageUri);

      if (result.error) {
        console.error('Upload failed:', result.error);
        setAvatarUri(previousUri);
        return;
      }

      if (result.url) {
        setAvatarUri(result.url);
        setHasUploadedPhoto(true);

        if (userProfile?.avatar_url) {
          ImageUploadService.cleanupOldAvatar(userProfile.avatar_url).catch(() => {});
        }

        await refreshProfile();
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      setAvatarUri(previousUri);
    } finally {
      setImageLoading(false);
      // Apple 5.1.1: only AFTER the OS prompt has been answered may we
      // surface a continue affordance. Setting this flag here covers all
      // three outcomes (granted+picked, granted+cancelled, denied) so the
      // user is never trapped on this screen.
      setPermissionPromptShown(true);
    }
  }, [imageLoading, user?.id, avatarUri, userProfile?.avatar_url, refreshProfile]);

  const handleGoToHome = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    // PRD §4 — `onboarding_completed` macro event. addiction_type / frequency /
    // goal_type come from userProfile when available; sanitize() drops nulls.
    import('@/utils/clarityEvents')
      .then(({ logOnboardingCompleted }) =>
        logOnboardingCompleted({
          uploaded_photo: hasUploadedPhoto,
          addiction_type: (userProfile as any)?.addiction_type ?? undefined,
          frequency: (userProfile as any)?.frequency ?? undefined,
          goal_type: (userProfile as any)?.goal_type ?? undefined,
        })
      )
      .catch(() => {});

    try {
      router.replace('/diary');

      setTimeout(async () => {
        try {
          await TempOnboardingService.clearData();
        } catch (error) {
          console.warn('Failed to clear temp data:', error);
        }
      }, 1000);
    } catch (error) {
      console.error('Navigation error:', error);
      setIsNavigating(false);
    }
  };

  return (
    <ImageBackground
      source={require('../assets/images/onboarding-success-bg.png')}
      style={styles.container}
      resizeMode="cover"
    >
      <SafeAreaView style={styles.safeArea} edges={['top', 'bottom']}>
        <View style={styles.body}>
          <View style={styles.contentGroup}>
            <View style={styles.headerSection}>
              <TouchableOpacity
                onPress={handleUploadPhoto}
                activeOpacity={0.8}
                disabled={imageLoading}
                style={styles.avatarTouchable}
              >
                <Avatar
                  uri={avatarUri}
                  size={112}
                  borderColor="#7E7D75"
                  borderWidth={1}
                  loading={imageLoading}
                  useCache={false}
                />
                {imageLoading && (
                  <View style={styles.avatarLoadingOverlay}>
                    <ActivityIndicator size="large" color="#7E7D75" />
                  </View>
                )}
              </TouchableOpacity>

              <Text style={styles.welcomeTitle}>
                <Text style={styles.welcomeTitleRegular}>
                  {t('onboarding.success.greeting')}{' '}
                </Text>
                <Text style={styles.welcomeTitleBold}>
                  {firstName}
                </Text>
                <Text style={styles.welcomeTitleRegular}> 👋</Text>
              </Text>
            </View>

            <View style={styles.subtitleGroup}>
              <Text style={styles.subtitleText}>
                <Text style={styles.subtitleRegular}>{t('onboarding.success.joinPrefix')}</Text>
                <Text style={styles.subtitleHighlight}>{t('onboarding.success.joinHighlight', { count: formatUserCount(userCount || FALLBACK_COUNT) })}</Text>
                <Text style={styles.subtitleRegular}>{t('onboarding.success.joinSuffix')}</Text>
              </Text>
            </View>
          </View>
        </View>

        <View style={[styles.buttonSection, { paddingBottom: insets.bottom + 24 }]}>
          {hasUploadedPhoto ? (
            <PrimaryButton
              title={t('onboarding.success.buttonContinue')}
              onPress={handleGoToHome}
              disabled={isNavigating}
              loading={isNavigating}
            />
          ) : (
            <>
              {/* Apple 5.1.1: on initial mount only this single primary action
                  is visible. No skip / "later" / continue affordance is
                  rendered until the OS permission prompt has been answered. */}
              <TouchableOpacity
                style={styles.uploadButton}
                onPress={handleUploadPhoto}
                activeOpacity={0.8}
                disabled={imageLoading}
              >
                <View style={styles.cameraIcon}>
                  <Svg width="19" height="17" viewBox="0 0 19 17" fill="none">
                    <Path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.29 0.3015C13.3 0.7035 13.609 2.1035 14.022 2.5535C14.435 3.0035 15.026 3.1565 15.353 3.1565C17.091 3.1565 18.5 4.5655 18.5 6.3025V12.0975C18.5 14.4275 16.61 16.3175 14.28 16.3175H4.22C1.889 16.3175 0 14.4275 0 12.0975V6.3025C0 4.5655 1.409 3.1565 3.147 3.1565C3.473 3.1565 4.064 3.0035 4.478 2.5535C4.891 2.1035 5.199 0.7035 6.209 0.3015C7.22 -0.1005 11.28 -0.1005 12.29 0.3015Z"
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="M14.7461 5.75H14.7551"
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.4283 9.37822C12.4283 7.62222 11.0053 6.19922 9.24931 6.19922C7.49331 6.19922 6.07031 7.62222 6.07031 9.37822C6.07031 11.1342 7.49331 12.5572 9.24931 12.5572C11.0053 12.5572 12.4283 11.1342 12.4283 9.37822Z"
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Svg>
                </View>
                <Text style={styles.uploadButtonText}>
                  {imageLoading ? t('onboarding.success.uploading') : t('onboarding.success.uploadPhoto')}
                </Text>
                {imageLoading && (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                )}
              </TouchableOpacity>

              {/* Apple 5.1.1: only render a continue affordance AFTER the OS
                  permission prompt has been answered. permissionPromptShown
                  flips to true in handleUploadPhoto's finally branch — never
                  on initial mount — so the user has no way to bypass the
                  prompt without first seeing it. */}
              {permissionPromptShown && (
                <PrimaryButton
                  title={t('onboarding.success.buttonContinue')}
                  onPress={handleGoToHome}
                  disabled={isNavigating}
                  loading={isNavigating}
                />
              )}
            </>
          )}
        </View>
      </SafeAreaView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
  },
  body: {
    flex: 1,
    paddingHorizontal: 40,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  contentGroup: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  headerSection: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
    width: '100%',
  },
  avatarTouchable: {
    position: 'relative',
  },
  avatarLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 56,
    backgroundColor: 'rgba(218, 216, 207, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  welcomeTitle: {
    textAlign: 'center',
    fontSize: 24,
    lineHeight: 28.8,
  },
  welcomeTitleRegular: {
    fontFamily: 'Sentient-Regular',
    color: '#151515',
  },
  welcomeTitleBold: {
    fontFamily: 'Sentient-Bold',
    color: '#151515',
  },
  subtitleGroup: {
    alignItems: 'center',
    gap: 8,
    width: '100%',
    paddingHorizontal: 24,
    marginTop: 8,
  },
  subtitleText: {
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
  subtitleRegular: {
    fontFamily: 'Inter_400Regular',
    color: '#151515',
  },
  subtitleHighlight: {
    fontFamily: 'Inter_700Bold',
    color: '#669AB7',
  },
  buttonSection: {
    paddingHorizontal: 24,
    gap: 8,
    width: '100%',
  },
  uploadButton: {
    backgroundColor: '#151515',
    borderRadius: 12,
    height: 56,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  cameraIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadButtonText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
});
